const configuredApiUrl = (process.env.REACT_APP_API_BASE_URL).replace(/\/$/, "");
const API_BASE_URL = configuredApiUrl.endsWith("/api/v1")
  ? configuredApiUrl
  : `${configuredApiUrl}/api/v1`;

export const getToken = () => localStorage.getItem("admin_token");

export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("admin_user"));
  } catch {
    return null;
  }
};

export const storeSession = ({ token, user }) => {
  localStorage.setItem("admin_token", token);
  localStorage.setItem("admin_user", JSON.stringify(user));
};

export const clearSession = () => {
  localStorage.removeItem("admin_token");
  localStorage.removeItem("admin_user");
  localStorage.removeItem("token");
};

export const apiRequest = async (path, options = {}) => {
  const token = getToken();
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers = {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  if (isFormData) {
    delete headers["Content-Type"];
    delete headers["content-type"];
  } else if (!headers["Content-Type"] && !headers["content-type"]) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const validationMessage = payload.errors
      ? Object.values(payload.errors).flat().find(Boolean)
      : null;
    const error = new Error(validationMessage || payload.message || "Unable to connect to the server.");
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
};

export const apiDownload = async (path, { filename = "download.csv", accept = "text/csv" } = {}) => {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: {
      Accept: accept,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const validationMessage = payload.errors
      ? Object.values(payload.errors).flat().find(Boolean)
      : null;
    const error = new Error(validationMessage || payload.message || "Unable to download file.");
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
  const downloadName = match ? decodeURIComponent(match[1]) : filename;

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = downloadName;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const login = async (email, password) => {
  const payload = await apiRequest("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  storeSession(payload.data);
  return payload.data;
};

export const resetPassword = async ({ email, token, password, passwordConfirmation }) => {
  const payload = await apiRequest("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({
      email,
      token,
      password,
      password_confirmation: passwordConfirmation,
    }),
  });

  return payload.data ?? payload;
};

export const forgotPassword = async (email) => {
  const payload = await apiRequest("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });

  return payload.data ?? payload;
};

export const logout = async () => {
  try {
    await apiRequest("/auth/logout", { method: "POST" });
  } catch {
    // A missing or expired token is already effectively logged out.
  } finally {
    clearSession();
  }
};

export const fetchCurrentUser = async () => {
  const payload = await apiRequest("/auth/me");
  localStorage.setItem("admin_user", JSON.stringify(payload.data));
  return payload.data;
};
